import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { API_BASE_URL } from '../api';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function DriverDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [driverJobs, setDriverJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalCopied, setPortalCopied] = useState(false);

  useEffect(() => {
    fetchDriverData();
  }, [id]);

  const fetchDriverData = async () => {
    try {
      const driverResponse = await fetch(`${API_BASE_URL}drivers/${id}`, {
        headers: authHeaders(),
      });
      if (!driverResponse.ok) {
        setDriver(null);
        setDriverJobs([]);
        return;
      }
      const driverData = await driverResponse.json();
      setDriver(driverData);

      const driverLabel = driverData?.name || driverData?.email || '';
      const qs = driverLabel ? `?driver=${encodeURIComponent(driverLabel)}` : '';
      const jobsResponse = await fetch(`${API_BASE_URL}collections${qs}`, {
        headers: authHeaders(),
      });
      const jobsPayload = await jobsResponse.json();
      const list = Array.isArray(jobsPayload)
        ? jobsPayload
        : jobsPayload?.data && Array.isArray(jobsPayload.data)
          ? jobsPayload.data
          : [];
      setDriverJobs(list);
    } catch (error) {
      console.error('Error fetching driver data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return { color: '#10b981', bg: '#dcfce7', icon: <CheckCircleIcon /> };
      case 'in_progress': return { color: '#f59e0b', bg: '#fef3c7', icon: <ScheduleIcon /> };
      case 'pending': return { color: '#64748b', bg: '#f1f5f9', icon: <ScheduleIcon /> };
      default: return { color: '#ef4444', bg: '#fee2e2', icon: <WarningIcon /> };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (!driver) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', mt: 4 }}>
        <Typography variant="h6" sx={{ color: '#64748b' }}>
          Driver not found
        </Typography>
      </Box>
    );
  }

  const completedJobs = driverJobs.filter(job => job.status === 'completed');
  const activeJobs = driverJobs.filter(job => job.status !== 'completed');

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton
          onClick={() => navigate('/fleet')}
          sx={{ color: '#64748b' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            {driver.name}
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Driver Profile & Job History
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Driver Info Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    backgroundColor: '#2563eb',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                  }}
                >
                  {driver.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {driver.name}
                  </Typography>
                  <Chip
                    label={driver.driver_type || 'Driver'}
                    size="small"
                    sx={{
                      backgroundColor: '#dcfce7',
                      color: '#166534',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Contact Info */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {driver.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon sx={{ color: '#64748b', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {driver.phone}
                    </Typography>
                  </Box>
                )}
                
                {driver.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon sx={{ color: '#64748b', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {driver.email}
                    </Typography>
                  </Box>
                )}
                
                {driver.address && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationIcon sx={{ color: '#64748b', fontSize: 18, mt: 0.2 }} />
                    <Typography variant="body2" sx={{ color: '#64748b', flex: 1 }}>
                      {driver.address}
                    </Typography>
                  </Box>
                )}
                
                {driver.birth_date && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ color: '#64748b', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {formatDate(driver.birth_date)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {driver.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {driver.notes}
                    </Typography>
                  </Box>
                </>
              )}

              {driver.portal_login_url && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                    Portal link (this driver only)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1.5 }}>
                    Opens the login page with email pre-filled. The driver enters their password on their device.
                    For new job notifications — after login use &quot;Enable notifications&quot; in the portal (web / PWA).
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(driver.portal_login_url);
                        setPortalCopied(true);
                        setTimeout(() => setPortalCopied(false), 2500);
                      } catch {
                        /* ignore */
                      }
                    }}
                  >
                    {portalCopied ? 'Copied' : 'Copy link'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Driver's Jobs */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ 
                  width: 40, height: 40, borderRadius: '10px', 
                  background: 'linear-gradient(135deg, #2563eb20 0%, #2563eb10 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#2563eb'
                }}>
                  <AssignmentIcon />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Driver's Jobs ({driverJobs.length})
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    All jobs assigned to this driver
                  </Typography>
                </Box>
              </Box>

              {/* Job Statistics */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ p: 2, textAlign: 'center', backgroundColor: '#f8fafc' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#2563eb' }}>
                      {driverJobs.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Total Jobs
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ p: 2, textAlign: 'center', backgroundColor: '#dcfce7' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
                      {completedJobs.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Completed
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ p: 2, textAlign: 'center', backgroundColor: '#fef3c7' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                      {activeJobs.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Active
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ p: 2, textAlign: 'center', backgroundColor: '#f1f5f9' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#64748b' }}>
                      {driver.experience_years || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Years Exp.
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* Jobs List */}
              {driverJobs.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {driverJobs.map((job) => {
                    const statusStyle = getStatusColor(job.status);
                    return (
                      <Card key={job.id} sx={{ '&:hover': { boxShadow: 2 } }}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                                {job.name || 'Unnamed Job'}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#64748b' }}>
                                {job.client || 'No client assigned'}
                              </Typography>
                            </Box>
                            <Chip
                              icon={statusStyle.icon}
                              label={job.status.replace('_', ' ')}
                              size="small"
                              sx={{
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.color,
                                fontWeight: 600,
                                textTransform: 'capitalize',
                                fontSize: '0.75rem',
                              }}
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LocationIcon sx={{ color: '#64748b', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                              {job.address || 'No address'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon sx={{ color: '#64748b', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                              {formatDate(job.scheduled_date)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    No jobs assigned to this driver yet.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
} 